import {
  getAgentSessionsTokenProvider,
  type AgentSessionsTokenProvider,
} from '@github-ui/copilot-auth-token/agent-sessions-token'
import type {AuthToken} from '@github-ui/copilot-auth-token/auth-token'
import {makeCAPIRequest} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import type {APIRequestFailure} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {getEnv} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const DEFAULT_CAPI_URL = 'https://api.githubcopilot.com'
const DEFAULT_CMC_GATEWAY_URL = 'https://api.github.com/cmc_internal/api'
const AGENT_SESSIONS_CLIENT_APPLICATION_HEADER = 'X-Client-Application'
const AGENT_SESSIONS_CLIENT_APPLICATION = 'copilot-cloud-web'

interface FetchWithAuthOptions {
  path: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  streamingResponse?: boolean
  basePath?: string
  body?: object
  retryOn401?: boolean
  copilotApiUrl?: string | undefined
  authToken?: AuthToken
  useCapi?: boolean
  /** Overrides the integration identity without changing the token provider or gateway routing. */
  integrationId?: string
  additionalHeaders?: Record<string, string>
  signal?: AbortSignal
}

/**
 * Get a user-friendly fallback message based on HTTP status code.
 * Used when the server doesn't provide a meaningful error message.
 */
function getFallbackMessage(status: number): string {
  switch (status) {
    case 0:
      return 'Network error. Please check your connection and try again.'
    case 400:
      return 'The request was invalid. Please verify your input and try again.'
    case 401:
      return "You don't have permission to perform this action. Please sign in and try again."
    case 402:
      return "You've run out of Copilot premium quota."
    case 403:
      return "This feature isn't available for this repository or organization."
    case 404:
      return "The requested resource couldn't be found."
    case 408:
      return 'The request timed out. Please try again.'
    case 409:
      return 'A conflict occurred. The resource may already exist.'
    case 422:
      return 'The request was rejected. A repository rule may have blocked this action.'
    case 429:
      return 'Too many requests. Please wait a moment and try again.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
}

const getIntegrationId = (): string => {
  // Handle the case where this code is running in a non-browser environment (e.g. during SSR, in tests without JSDOM, etc.)
  if (typeof window === 'undefined') {
    return 'copilot-developer'
  }

  if (copilotFeatureFlags.webIntegrationCutover) {
    return 'copilot-web'
  }

  const {hostname, port} = window.location
  // Proxima runs locally on port 3100 but uses the production integration.
  if (port !== '3100' && (hostname === 'localhost' || hostname.endsWith('.localhost'))) {
    return 'copilot-developer-dev'
  }

  // Otherwise, use the default production integration
  return 'copilot-developer'
}

export class FetchCAPIError extends Error {
  status: number
  originalMessage: string
  /** A readable clone of a conflict response, when one was received. */
  response?: Response
  activationContext?: FetchCAPIActivationContext
  connectionTraceparent?: string
  /** Validated machine-readable error code returned by the API. */
  code?: string
  /** Whether the request received an HTTP response. */
  hasResponse: boolean
  /** Validated server message retained for diagnostics when user-facing copy stays generic. */
  responseMessage?: string
  /** Safe response metadata useful for correlation and retry decisions. */
  requestId?: string
  retryAfter?: string
  requestFailure?: APIRequestFailure
  /** Structured validation errors returned by the API (422 responses) */
  errors?: Array<{code: string; message?: string}>
  /** Documentation URL for the error */
  documentationUrl?: string

  constructor(
    message: string,
    status: number,
    errors?: Array<{code: string; message?: string}>,
    documentationUrl?: string,
    metadata: {
      response?: Response
      activationContext?: FetchCAPIActivationContext
      connectionTraceparent?: string
      code?: string
      hasResponse?: boolean
      responseMessage?: string
      requestId?: string
      retryAfter?: string
      requestFailure?: APIRequestFailure
    } = {},
  ) {
    super(message)
    this.name = 'FetchCAPIError'
    this.status = status
    this.originalMessage = message
    this.response = metadata.response
    this.activationContext = metadata.activationContext
    this.connectionTraceparent = metadata.connectionTraceparent
    this.code = metadata.code
    this.hasResponse = metadata.hasResponse ?? true
    this.responseMessage = metadata.responseMessage
    this.requestId = metadata.requestId
    this.retryAfter = metadata.retryAfter
    this.requestFailure = metadata.requestFailure
    this.errors = errors
    this.documentationUrl = documentationUrl
  }
}

export interface FetchCAPIActivationContext {
  version?: number
  sequence?: string | number
  traceparent?: string
  tracestate?: string
}

/*
 * NOTE: The agents sessions service is in the process of moving out of CAPI, and to our own service.
 * This means that in the future, we will be fetching all `/agents` paths from api.github.com/cmc_internal/api,
 * NOT, CAPI (api.githubcopilot.com).
 * When the feature flag copilot_mission_control_use_api_gateway has finished rolling out, we will rename this function appropriately.
 * In the meantime, if you have a reason to want to use this function to actually fetch to CAPI (non /agents routes),
 * please come talk to us in #mission-control-team.
 *
 */
export async function fetchCAPI({
  path,
  method = 'GET',
  streamingResponse = false,
  additionalHeaders,
  body,
  retryOn401,
  copilotApiUrl,
  authToken,
  useCapi = false,
  integrationId,
  signal,
}: FetchWithAuthOptions): Promise<Response> {
  const tokenProvider = authToken ? undefined : getAgentSessionsTokenProvider(useCapi)

  // The gateway flag also selects which token AgentSessionsTokenProvider mints, and CAPI and
  // mission control encrypt those tokens with different keys. Sending a mission control token to
  // CAPI fails decryption and returns a 400, so the flag has to win over a per-call base path.
  let basePath: string
  if (useCapi) {
    basePath = copilotApiUrl ?? getEnv().copilotApiOverrideUrl ?? DEFAULT_CAPI_URL
  } else if (isFeatureEnabled('copilot_mission_control_use_api_gateway')) {
    basePath = getEnv().cmcApiUrl ?? DEFAULT_CMC_GATEWAY_URL
  } else if (copilotApiUrl) {
    basePath = copilotApiUrl
  } else {
    basePath = getEnv().copilotApiOverrideUrl ?? DEFAULT_CAPI_URL
  }

  const shouldRetryOn401 = retryOn401 ?? true
  const requestHeaders = addAgentSessionsClientHeaders(path, additionalHeaders)

  return requestWithAuthRetry(
    {
      path,
      method,
      streamingResponse,
      additionalHeaders: requestHeaders,
      body,
      basePath,
      retryOn401: shouldRetryOn401,
      authToken,
      integrationId,
      signal,
    },
    tokenProvider,
  )
}

function addAgentSessionsClientHeaders(
  path: string,
  additionalHeaders?: Record<string, string>,
): Record<string, string> | undefined {
  if (path !== '/agents' && !path.startsWith('/agents/')) {
    return additionalHeaders
  }

  const headers = Object.fromEntries(
    Object.entries(additionalHeaders ?? {}).filter(
      ([header]) => header.toLowerCase() !== AGENT_SESSIONS_CLIENT_APPLICATION_HEADER.toLowerCase(),
    ),
  )

  return {...headers, [AGENT_SESSIONS_CLIENT_APPLICATION_HEADER]: AGENT_SESSIONS_CLIENT_APPLICATION}
}

interface RequestWithRetryOptions {
  authToken?: AuthToken
  integrationId?: string
  path: string
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  streamingResponse: boolean
  additionalHeaders?: Record<string, string>
  basePath: string
  body?: object
  retryOn401: boolean
  signal?: AbortSignal
}

const MAX_AUTH_RETRIES = 1

interface ParsedCAPIErrorBody {
  message?: string
  code?: string
  errors?: Array<{code: string; message?: string}>
  documentationUrl?: string
}

function activationContextFromHeaders(headers: Headers | undefined): FetchCAPIActivationContext | undefined {
  if (!headers) return undefined
  const traceparent = headers.get('X-Copilot-Activation-Traceparent')
  if (!traceparent) return undefined
  const sequence = headers.get('X-Copilot-Activation-Sequence')
  const tracestate = headers.get('X-Copilot-Activation-Tracestate')
  return {
    version: 2,
    traceparent,
    ...(sequence ? {sequence} : {}),
    ...(tracestate ? {tracestate} : {}),
  }
}

function parseStructuredErrors(value: unknown): Array<{code: string; message?: string}> | undefined {
  if (!Array.isArray(value)) return undefined
  const errors = value.flatMap(item => {
    if (!item || typeof item !== 'object' || !('code' in item) || typeof item.code !== 'string') return []
    const message = 'message' in item && typeof item.message === 'string' ? item.message : undefined
    return [{code: item.code, ...(message ? {message} : {})}]
  })
  return errors.length > 0 ? errors : undefined
}

function parseCAPIErrorBody(text: string): ParsedCAPIErrorBody {
  const trimmed = text.trim()
  if (!trimmed) return {}
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (!parsed || typeof parsed !== 'object') return {message: trimmed}
    return {
      ...('message' in parsed && typeof parsed.message === 'string' ? {message: parsed.message} : {}),
      ...('code' in parsed && typeof parsed.code === 'string' ? {code: parsed.code} : {}),
      ...('errors' in parsed ? {errors: parseStructuredErrors(parsed.errors)} : {}),
      ...('documentation_url' in parsed && typeof parsed.documentation_url === 'string'
        ? {documentationUrl: parsed.documentation_url}
        : {}),
    }
  } catch {
    return {message: trimmed}
  }
}

async function requestWithAuthRetry(
  requestOptions: RequestWithRetryOptions,
  tokenProvider: AgentSessionsTokenProvider | undefined,
  retryAttempt = 0,
): Promise<Response> {
  const token = requestOptions.authToken ?? (await tokenProvider?.getAuthToken())

  if (!token) {
    throw new Error('No token available')
  }

  const integrationId = requestOptions.integrationId ?? getIntegrationId()

  const response = await makeCAPIRequest({
    authToken: token,
    basePath: requestOptions.basePath,
    method: requestOptions.method,
    path: requestOptions.path,
    streamingResponse: requestOptions.streamingResponse,
    body: requestOptions.body,
    integrationId,
    additionalHeaders: requestOptions.additionalHeaders,
    signal: requestOptions.signal,
  })

  if (!response.ok) {
    if (tokenProvider && requestOptions.retryOn401 && response.status === 401 && retryAttempt < MAX_AUTH_RETRIES) {
      // On 401, the access token was rejected by CAPI. Try to refresh the token first
      const refreshed = await tokenProvider.tryRefreshToken()
      if (!refreshed) {
        tokenProvider.removeLocalStorageAuthToken()
      }
      return requestWithAuthRetry(requestOptions, tokenProvider, retryAttempt + 1)
    }

    const actualResponse = 'response' in response ? response.response : undefined
    const activationContext = activationContextFromHeaders(actualResponse?.headers)
    let retainedResponse: Response | undefined
    if (response.status === 409) {
      try {
        retainedResponse = actualResponse?.clone()
      } catch {
        // Preserve the original FetchCAPIError when a response body cannot be cloned.
      }
    }
    let parsedBody: ParsedCAPIErrorBody = {}
    if (actualResponse) {
      try {
        parsedBody = parseCAPIErrorBody(await actualResponse.text())
      } catch {
        parsedBody = {}
      }
    }

    const fallbackMessage = 'error' in response ? response.error : getFallbackMessage(response.status)
    const errorMessage =
      response.status >= 500 ? getFallbackMessage(response.status) : (parsedBody.message ?? fallbackMessage)
    const requestId = actualResponse?.headers.get('X-GitHub-Request-ID')
    const retryAfter = actualResponse?.headers.get('Retry-After')
    throw new FetchCAPIError(errorMessage, response.status, parsedBody.errors, parsedBody.documentationUrl, {
      ...(isFeatureEnabled('one_copilot_connection_tracing')
        ? {connectionTraceparent: actualResponse?.headers.get('X-Copilot-Connection-Traceparent') ?? undefined}
        : {}),
      ...(activationContext ? {activationContext} : {}),
      ...(retainedResponse ? {response: retainedResponse} : {}),
      ...(parsedBody.code ? {code: parsedBody.code} : {}),
      hasResponse: Boolean(actualResponse),
      ...(parsedBody.message ? {responseMessage: parsedBody.message} : {}),
      ...(requestId ? {requestId} : {}),
      ...(retryAfter ? {retryAfter} : {}),
      ...('requestFailure' in response && response.requestFailure ? {requestFailure: response.requestFailure} : {}),
    })
  }

  return response
}
